/* MakerMade MM Bottom Bar — Oct 15, 2026. Maintenance notes: mm-signup-bar.liquid. */
if (!customElements.get('mm-signup-bar')) {
  customElements.define('mm-signup-bar', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const bar = this.querySelector('.mm-signup-bar');
      if (!bar) return;
      const dialog = this.querySelector('dialog');
      const slot = this.querySelector('[data-mm-form-slot]');
      const help = this.querySelector('.mm-signup-help');
      const trigger = this.querySelector('.mm-signup-trigger');
      const status = this.querySelector('.mm-action-status');
      const editor = this.dataset.design === 'true';
      const mode = this.dataset.mode || 'spoks';
      const legacyKey = `mm-signup:${this.dataset.campaign}:${this.dataset.target}`;
      const key = `mm-bottom:v2:${JSON.stringify([this.dataset.campaign, mode, this.dataset.target])}`;
      const abort = new AbortController();
      const listen = (el, event, fn, options = {}) => el?.addEventListener(event, fn, { ...options, signal: abort.signal });
      const read = k => { try { const v = JSON.parse(localStorage.getItem(k) || '{}'); return v && typeof v === 'object' ? v : {}; } catch { return {}; } };
      let state = read(key), loaded = false, completed = false, copyBusy = false;
      let loadingTimer, checkTimer, closeTimer, actionTimer, statusTimer, previousOverflow;
      const write = () => { if (!editor) try { localStorage.setItem(key, JSON.stringify(state)); } catch { /* Memory state remains active. */ } };
      if (!state.kind && mode === 'spoks') {
        const old = read(legacyKey);
        if (old.subscribed) { state = { kind: 'action', at: Date.now() }; write(); }
        else if (old.until > Date.now()) { state = { kind: 'legacy', until: old.until }; write(); }
      }
      const days = kind => Math.max(0, Number(kind === 'action' ? this.dataset.actionDays : this.dataset.days) || 0);
      const suppressed = () => {
        if (editor) return false;
        if (state.kind === 'legacy') return state.until > Date.now();
        if (!['action','dismiss'].includes(state.kind) || !Number.isFinite(state.at)) return false;
        return days(state.kind) === 0 || Date.now() < state.at + days(state.kind) * 86400000;
      };
      const save = kind => { state = { kind, at: Date.now() }; write(); sync(); };
      // Scheduling requires an explicit timezone so visitors see the same campaign window.
      const parseTime = value => !value?.trim() ? null : /(?:Z|[+-]\d{2}:\d{2})$/i.test(value.trim()) ? Date.parse(value) : NaN;
      const start = parseTime(this.dataset.start), end = parseTime(this.dataset.end);
      const eligible = () => {
        const now = Date.now();
        if (Number.isNaN(start) || Number.isNaN(end) || (start !== null && now < start) || (end !== null && now >= end)) return false;
        const rule = this.dataset.pageRule;
        if (!rule || rule === 'all') return true;
        const path = window.location.pathname.replace(/\/$/, '') || '/';
        const paths = (this.dataset.paths || '').split(/\r?\n/).map(p => p.trim()).filter(Boolean);
        const match = paths.some(p => p.endsWith('*') ? path.startsWith(p.slice(0,-1)) : path === (p.replace(/\/$/, '') || '/'));
        return rule === 'include' ? match : !match;
      };
      const visible = el => {
        if (!el || !el.getClientRects().length) return false;
        const css = getComputedStyle(el), r = el.getBoundingClientRect();
        return css.display !== 'none' && css.visibility !== 'hidden' && r.height > 0 && r.top < window.innerHeight && r.bottom > 0;
      };
      const spacer = document.createElement('div'); spacer.setAttribute('aria-hidden','true'); this.append(spacer);
      const sync = () => {
        const purchases = [...document.querySelectorAll('sticky-cart.is-visible, .js-custom-bundle-bar.is-pinned')].filter(visible);
        const dock = document.getElementById('mobile-dock');
        let occupied = visible(dock) ? Math.max(0, window.innerHeight - dock.getBoundingClientRect().top) : 0;
        if (this.dataset.purchase !== 'hide') purchases.forEach(el => { occupied = Math.max(occupied, window.innerHeight - el.getBoundingClientRect().top); });
        this.style.setProperty('--mm-occupied', `${occupied}px`);
        const blocked = document.body.classList.contains('drawer-open') || document.querySelector('modal-dialog[open], .modal-dialog-component[open]');
        bar.hidden = suppressed() || !eligible() || !!blocked || (this.dataset.purchase === 'hide' && purchases.length > 0);
        const bottom = Number.parseFloat(getComputedStyle(bar).bottom) || 0;
        // Athens reserves its purchase-bar height separately; reserve only our extra area.
        spacer.style.height = bar.hidden ? '0px' : `${bar.getBoundingClientRect().height + Math.max(0, bottom - occupied)}px`;
      };
      const close = () => { if (dialog?.open) dialog.close(); };
      const roots = () => slot ? [...slot.querySelectorAll('*')].map(el => el.shadowRoot).filter(Boolean) : [];
      const findForm = () => roots().some(root => root.querySelector('form,input,button'));
      const inspect = () => {
        if (findForm() && help) help.hidden = true;
        const expected = (this.dataset.success || '').trim();
        if (!expected || completed || !dialog?.open) return;
        const confirmed = roots().some(root => [...root.querySelectorAll('*')].some(el =>
          el.children.length === 0 && el.textContent.trim() === expected && el.getClientRects().length > 0
        ));
        if (!confirmed) return;
        completed = true; save('action');
        clearInterval(checkTimer); clearTimeout(loadingTimer);
        closeTimer = setTimeout(close, 2200);
      };
      const open = () => {
        if (!dialog || dialog.open) return;
        previousOverflow = document.body.style.overflow;
        dialog.showModal(); document.body.style.overflow = 'hidden';
        if (mode === 'info') { if (this.dataset.hideAction === 'true') save('action'); return; }
        if (!loaded) {
          loaded = true; completed = false;
          const host = document.createElement('div'); host.setAttribute('data-spoks-form-id', slot.dataset.formId);
          slot.append(host); // Spoks mounts its embedded form in an open shadow root.
        }
        clearTimeout(loadingTimer);
        loadingTimer = setTimeout(() => { help.hidden = !!findForm(); }, 12000);
        clearInterval(checkTimer); checkTimer = setInterval(inspect, 300); inspect();
      };
      listen(trigger, 'click', async e => {
        if (mode === 'link') {
          // Keep native navigation, including modifier clicks and target=_blank.
          if (this.dataset.hideAction === 'true') save('action');
        } else if (mode === 'copy') {
          if (copyBusy) return;
          copyBusy = true;
          try {
            if (!this.dataset.target?.trim()) throw Error('Missing code');
            await navigator.clipboard.writeText(this.dataset.target);
            status.textContent = 'Code copied';
            clearTimeout(statusTimer); statusTimer = setTimeout(() => { status.textContent = ''; }, 4000);
            if (this.dataset.hideAction === 'true') actionTimer = setTimeout(() => save('action'), 1200);
          } catch { status.textContent = `Unable to copy automatically. Your code: ${this.dataset.target || 'not configured'}`; }
          finally { copyBusy = false; }
        } else { e.preventDefault(); open(); }
      });
      listen(this.querySelector('.mm-signup-dismiss'), 'click', () => { clearTimeout(actionTimer); clearTimeout(statusTimer); status.textContent = ''; save('dismiss'); });
      listen(this.querySelector('.mm-signup-close'), 'click', close);
      listen(dialog, 'click', e => {
        if (e.target !== dialog) return;
        const r = dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
      });
      listen(dialog, 'close', () => {
        if (previousOverflow !== undefined) { document.body.style.overflow = previousOverflow; previousOverflow = undefined; }
        clearInterval(checkTimer); clearTimeout(loadingTimer); clearTimeout(closeTimer);
        if (completed && slot) { slot.replaceChildren(); loaded = false; completed = false; }
        if (!bar.hidden) trigger?.focus();
      });
      listen(this.querySelector('[data-mm-reset]'), 'click', () => {
        try { localStorage.removeItem(key); localStorage.removeItem(legacyKey); } catch { /* No persistent storage. */ }
        state = {}; status.textContent = ''; sync();
      });
      listen(window, 'storage', e => { if (e.key === key || e.key === null) { state = read(key); sync(); } });
      listen(window, 'resize', sync);
      const resize = new ResizeObserver(sync); resize.observe(bar);
      const interval = setInterval(sync, 250); sync();
      this.cleanup = () => {
        close(); if (previousOverflow !== undefined) document.body.style.overflow = previousOverflow;
        abort.abort(); resize.disconnect(); clearInterval(interval); clearInterval(checkTimer);
        [loadingTimer, closeTimer, actionTimer, statusTimer].forEach(clearTimeout); spacer.remove(); this.cleanup = null;
      };
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
}
